<template>
  <!-- Los datos de entrada de una herramienta de fórmula en el diseño nuevo (#961, Baja Tensión): sus
       tipos de inspección y las secciones de su definición, con los mismos campos y las mismas
       condiciones de visibilidad que el diseño actual. Lo propio de RITE (la matriz y las bandas de
       frío) sigue en `RiteInstalacion`; esto no sabe de ninguna herramienta en concreto y pinta lo que
       traiga la definición, que es contenido y no código. -->
  <div class="cdh">
    <CalcPanel v-if="tipos.length" titulo="Tipo de inspección">
      <div class="cdh-tipos" role="group" aria-label="Tipo de inspección">
        <button v-for="t in tipos" :key="t.clave" type="button" class="cdh-tipo" :aria-pressed="String(t.clave === srv)" @click="$emit('srv', t.clave)">
          <span v-if="t.icono" class="cdh-ico" aria-hidden="true">{{ t.icono }}</span>
          <span class="cdh-tx"><b>{{ t.nombre }}</b><small v-if="t.norma">{{ t.norma }}</small></span>
        </button>
      </div>
      <p v-if="descripcion" class="cdn-note">{{ descripcion }}</p>
    </CalcPanel>

    <CalcPanel v-for="(sec, i) in bloques" :key="'s' + i" :titulo="sec.titulo || 'Datos de la instalación'">
      <div v-if="sec.campos.length" class="cdn-grid">
        <template v-for="w in sec.campos">
          <div v-if="w.tipo === 'number'" :key="w.campo" class="cdn-f">
            <label :for="'cdh-' + w.campo">{{ w.label || w.campo }}<span v-if="w.required" class="cdh-rq" title="Obligatorio"> *</span></label>
            <input :id="'cdh-' + w.campo" type="number" :min="w.min != null ? w.min : 0" :step="w.step || 1" :value="form[w.campo]" @input="num(w.campo, $event)">
            <span v-if="w.hint" class="cdn-ht">{{ w.hint }}</span>
          </div>
          <div v-else-if="w.tipo === 'select'" :key="w.campo" class="cdn-f">
            <label :for="'cdh-' + w.campo">{{ w.label || w.campo }}</label>
            <select :id="'cdh-' + w.campo" :value="form[w.campo]" @change="$emit('set', w.campo, $event.target.value)">
              <option v-for="o in w.options" :key="o.key" :value="o.key">{{ o.label || o.nombre }}</option>
            </select>
          </div>
          <!-- Las tres formas de «elige una opción» del diseño actual (pareja, tarjetas y niveles) se
               pintan igual aquí: escriben la misma clave en el mismo campo. -->
          <div v-else-if="OPCIONES.indexOf(w.tipo) >= 0" :key="w.campo" class="cdn-f cdh-ancho">
            <label>{{ w.label || w.campo }}</label>
            <div class="cdh-ops" role="group" :aria-label="w.label || w.campo">
              <template v-for="o in w.options">
                <button v-if="!o.visible || visible(o)" :key="o.key" type="button" class="cdh-op" :aria-pressed="String(form[w.campo] === o.key)" @click="$emit('set', w.campo, o.key)">
                  <span v-if="o.icono" aria-hidden="true">{{ o.icono }} </span>{{ o.label || o.nombre }}<small v-if="o.sub">{{ o.sub }}</small>
                </button>
              </template>
            </div>
          </div>
          <div v-else-if="w.tipo === 'checkboxCard'" :key="w.campo" class="cdn-f">
            <label class="cdn-chk" :class="{ on: !!form[w.campo] }"><input type="checkbox" :checked="!!form[w.campo]" @change="$emit('set', w.campo, $event.target.checked)">{{ etiquetaMarca(w) }}</label>
            <template v-if="form[w.campo] && w.subInput">
              <label :for="'cdh-' + w.subInput.campo">{{ w.subInput.label }}</label>
              <input :id="'cdh-' + w.subInput.campo" type="number" :min="w.subInput.min || 0" :step="w.subInput.step || 1" :value="form[w.subInput.campo]" @input="num(w.subInput.campo, $event)">
            </template>
          </div>
          <div v-else-if="w.tipo === 'tableCheckboxes'" :key="w.campo" class="cdn-f cdh-ancho">
            <label>{{ w.label || w.campo }}</label>
            <div class="cdn-checks">
              <label v-for="row in w.rows" :key="row.key" class="cdn-chk" :class="{ on: marcada(w.campo, row.key) }">
                <input type="checkbox" :checked="marcada(w.campo, row.key)" @change="$emit('fila', w.campo, row.key)">{{ row.label }}<span v-if="marcada(w.campo, row.key) && row.tpcLabel" class="cdh-tpc">{{ row.tpcLabel }}</span>
              </label>
            </div>
          </div>
        </template>
      </div>
      <div v-if="sec.marcas.length" class="cdn-checks" :style="sec.campos.length ? 'margin-top:12px' : ''">
        <label v-for="w in sec.marcas" :key="w.campo" class="cdn-chk" :class="{ on: !!form[w.campo] }"><input type="checkbox" :checked="!!form[w.campo]" @change="$emit('set', w.campo, $event.target.checked)">{{ etiquetaMarca(w) }}</label>
      </div>
    </CalcPanel>
  </div>
</template>

<script>
import CalcPanel from './CalcPanel.vue';

const OPCIONES = ['togglePair', 'radioCard', 'levelSelector'];
// Los tipos de widget que pinta el diseño actual. Uno que no esté aquí tampoco se pintaba allí.
const CAMPOS = ['number', 'select', 'checkboxCard', 'tableCheckboxes'].concat(OPCIONES);

export default {
  components: { CalcPanel },
  props: {
    /** Los tipos de inspección de la herramienta: `[{ clave, nombre, norma, icono }]`. */
    tipos: { type: Array, default: function () { return []; } },
    srv: { type: String, default: '' },
    /** La descripción del tipo elegido, la misma que enseña el diseño actual debajo de las tarjetas. */
    descripcion: { type: String, default: '' },
    /** Las secciones de la definición, ya filtradas por su condición de visibilidad. */
    secciones: { type: Array, default: function () { return []; } },
    form: { type: Object, required: true },
    /**
     * La condición de visibilidad de cada campo, la de la pantalla (`isVisible`): se pasa en vez de
     * evaluarla aquí para que los dos diseños no puedan discrepar sobre qué campo se ve.
     */
    visible: { type: Function, default: function () { return true; } }
  },
  data: function () {
    return { OPCIONES: OPCIONES };
  },
  computed: {
    /**
     * Cada sección con sus campos visibles, en su orden, separados en dos grupos: las casillas
     * sueltas (`checkbox`) van juntas en una fila de casillas, como «Gastos» o los recargos del
     * diseño nuevo, y el resto en la rejilla de campos.
     */
    bloques: function () {
      const self = this;
      return this.secciones.map(function (sec) {
        const ws = (sec.widgets || []).filter(function (w) { return w && w.campo && self.visible(w); });
        return {
          titulo: sec.titulo,
          campos: ws.filter(function (w) { return CAMPOS.indexOf(w.tipo) >= 0; }),
          marcas: ws.filter(function (w) { return w.tipo === 'checkbox'; })
        };
      }).filter(function (b) { return b.campos.length || b.marcas.length; });
    }
  },
  methods: {
    num: function (campo, ev) {
      this.$emit('set', campo, parseFloat(ev.target.value) || 0);
    },
    etiquetaMarca: function (w) {
      return (w.icono ? w.icono + ' ' : '') + (w.label || w.campo);
    },
    marcada: function (campo, clave) {
      return !!(this.form[campo] && this.form[campo][clave]);
    }
  }
};
</script>

<style scoped>
.cdh{display:flex;flex-direction:column;gap:14px;min-width:0}
.cdh-tipos{display:grid;grid-template-columns:repeat(auto-fill,minmax(170px,1fr));gap:8px}
.cdh-tipo{border:1.5px solid var(--line);background:var(--surface);color:var(--fg);border-radius:9px;padding:10px 12px;text-align:left;cursor:pointer;display:flex;align-items:center;gap:10px;min-width:0}
.cdh-tipo:hover{border-color:var(--accent)}
.cdh-tipo[aria-pressed="true"]{border-color:var(--accent);background:var(--accent-soft)}
.cdh-ico{font-size:20px;line-height:1;flex:none}
.cdh-tx{display:flex;flex-direction:column;gap:2px;min-width:0}
.cdh-tx b{font-size:13px;overflow-wrap:anywhere}
.cdh-tx small{font-size:11px;color:var(--muted)}
.cdh-rq{color:var(--bad)}
.cdh-ancho{grid-column:1/-1}
.cdh-ops{display:flex;flex-wrap:wrap;gap:6px}
.cdh-op{border:1px solid var(--line);background:var(--surface);color:var(--fg);border-radius:8px;padding:7px 11px;font-size:13px;cursor:pointer;display:inline-flex;flex-direction:column;align-items:flex-start;gap:2px}
.cdh-op small{font-size:11px;color:var(--muted)}
.cdh-op[aria-pressed="true"]{border-color:var(--accent);background:var(--accent-soft)}
.cdh-tpc{margin-left:6px;font-size:11px;font-weight:700;color:var(--accent)}
</style>
